import type { Highlight, Period } from '@casetrace/shared';
import { createTimeScale, type TimeScale } from './time-scale';

export interface StoryHighlight {
  readonly kind: 'highlight';
  readonly id: string;
  readonly epochMs: number;
  readonly position: number;
  readonly highlight: Highlight;
}

export interface StoryPeriod {
  readonly id: string;
  readonly period: Period;
  readonly fromPosition: number;
  readonly toPosition: number;
}

export interface StoryModel {
  readonly items: readonly StoryHighlight[];
  readonly periods: readonly StoryPeriod[];
  readonly scale: TimeScale;
  readonly length: number;
}

export function buildStoryModel(
  highlights: readonly Highlight[],
  periods: readonly Period[],
): StoryModel {
  const scaleInputs = collectScaleInputs(highlights, periods);
  const scale = createTimeScale(scaleInputs);
  const items = buildItems(highlights, scale);
  const periodItems = buildPeriods(periods, scale);

  return {
    items,
    periods: periodItems,
    scale,
    length: scale.length,
  };
}

/** Indeksvindu for elementer innenfor +/- radius posisjonsenheter. Binaersok, ikke lineaer skanning. */
export function visibleRange(
  model: StoryModel,
  position: number,
  radius: number,
): { readonly start: number; readonly end: number } {
  if (model.items.length === 0) return { start: 0, end: 0 };
  const safeRadius = Math.max(0, radius);
  const minPos = position - safeRadius;
  const maxPos = position + safeRadius;
  const start = lowerBoundByPosition(model.items, minPos);
  const end = upperBoundByPosition(model.items, maxPos);
  return { start, end };
}

/** Periodene som omslutter en gitt posisjon. */
export function activePeriods(model: StoryModel, position: number): readonly StoryPeriod[] {
  return model.periods.filter(
    (period) => period.fromPosition <= position && period.toPosition >= position,
  );
}

function collectScaleInputs(
  highlights: readonly Highlight[],
  periods: readonly Period[],
): number[] {
  const times: number[] = [];
  for (const highlight of highlights) {
    const ms = Date.parse(highlight.timestamp);
    if (Number.isFinite(ms)) times.push(ms);
  }
  for (const period of periods) {
    const fromMs = Date.parse(period.from);
    const toMs = Date.parse(period.to);
    if (Number.isFinite(fromMs)) times.push(fromMs);
    if (Number.isFinite(toMs)) times.push(toMs);
  }
  return times.sort((a, b) => a - b);
}

function buildItems(highlights: readonly Highlight[], scale: TimeScale): StoryHighlight[] {
  const items: StoryHighlight[] = [];
  for (const highlight of highlights) {
    const epochMs = Date.parse(highlight.timestamp);
    if (!Number.isFinite(epochMs)) continue;
    items.push({
      kind: 'highlight',
      id: highlight.eventId,
      epochMs,
      position: scale.toPosition(epochMs),
      highlight,
    });
  }
  items.sort(
    (a, b) => a.position - b.position || a.epochMs - b.epochMs || a.id.localeCompare(b.id),
  );
  return items;
}

function buildPeriods(periods: readonly Period[], scale: TimeScale): StoryPeriod[] {
  const out: StoryPeriod[] = [];
  for (const period of periods) {
    const fromMs = Date.parse(period.from);
    const toMs = Date.parse(period.to);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) continue;
    const fromPosition = scale.toPosition(fromMs);
    const toPosition = scale.toPosition(toMs);
    out.push({
      id: period.id,
      period,
      fromPosition: Math.min(fromPosition, toPosition),
      toPosition: Math.max(fromPosition, toPosition),
    });
  }
  out.sort(
    (a, b) =>
      a.fromPosition - b.fromPosition || a.toPosition - b.toPosition || a.id.localeCompare(b.id),
  );
  return out;
}

function lowerBoundByPosition(items: readonly StoryHighlight[], target: number): number {
  let low = 0;
  let high = items.length;
  while (low < high) {
    const mid = low + ((high - low) >> 1);
    if (items[mid].position < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

function upperBoundByPosition(items: readonly StoryHighlight[], target: number): number {
  let low = 0;
  let high = items.length;
  while (low < high) {
    const mid = low + ((high - low) >> 1);
    if (items[mid].position <= target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}
